import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { leadsAPI } from '@/lib/api';
import { ImportResult, ImportOptions } from '@/types/leads';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';

interface ImportLeadsModalProps {
  open: boolean;
  onClose: () => void;
  teamId: number;
}

export function ImportLeadsModal({ open, onClose, teamId }: ImportLeadsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [options, setOptions] = useState<ImportOptions>({
    skipHeader: true,
    delimiter: ',',
    overrideExisting: false,
  });
  const [result, setResult] = useState<ImportResult | null>(null);
  const [step, setStep] = useState<'upload' | 'options' | 'importing' | 'results'>('upload');
  
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      return leadsAPI.importLeads(teamId, file, options);
    },
    onSuccess: (data) => {
      setResult(data.data);
      setStep('results');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
    onError: (error) => {
      console.error('Import failed:', error);
      setResult({
        totalRows: 0,
        successCount: 0,
        failedCount: 0,
        updatedCount: 0,
        duplicateCount: 0,
        errors: [{ row: 0, errors: ['Import failed: ' + (error as Error).message] }],
        message: 'Import failed',
      });
      setStep('results');
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const csvFile = acceptedFiles[0];
    if (csvFile && csvFile.type === 'text/csv') {
      setFile(csvFile);
      setStep('options');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const handleImport = () => {
    setStep('importing');
    importMutation.mutate();
  };

  const handleReset = () => {
    setFile(null);
    setOptions({
      skipHeader: true,
      delimiter: ',',
      overrideExisting: false,
    });
    setResult(null);
    setStep('upload');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Leads from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {step === 'upload' && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    {isDragActive ? (
                      <p className="text-primary">Drop the CSV file here...</p>
                    ) : (
                      <div>
                        <p className="text-lg font-medium mb-2">Drop your CSV file here</p>
                        <p className="text-muted-foreground mb-4">or click to browse files</p>
                        <Button variant="outline">Choose File</Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Your CSV should include columns: email (required), name, company, jobTitle, phone, website, industry, etc.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {step === 'options' && file && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
                <span className="font-medium">{file.name}</span>
                <span className="text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="delimiter">CSV Delimiter</Label>
                  <Input
                    id="delimiter"
                    value={options.delimiter}
                    onChange={(e) => setOptions({ ...options, delimiter: e.target.value })}
                    placeholder=","
                    className="w-20"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="skipHeader"
                    checked={options.skipHeader || false}
                    onCheckedChange={(checked) => setOptions({ ...options, skipHeader: !!checked })}
                  />
                  <Label htmlFor="skipHeader">Skip header row</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="overrideExisting"
                    checked={options.overrideExisting || false}
                    onCheckedChange={(checked) => setOptions({ ...options, overrideExisting: !!checked })}
                  />
                  <Label htmlFor="overrideExisting">Override existing leads with same email</Label>
                </div>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">
                  Back
                </Button>
                <Button onClick={handleImport} className="flex-1">
                  Import Leads
                </Button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="space-y-4 text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <h3 className="text-lg font-medium">Importing leads...</h3>
              <p className="text-muted-foreground">This may take a few moments</p>
            </div>
          )}

          {step === 'results' && result && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    {result.successCount > 0 ? (
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    ) : (
                      <XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
                    )}
                    <h3 className="text-lg font-medium">{result.message}</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{result.successCount}</div>
                      <div className="text-muted-foreground">Imported</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{result.updatedCount}</div>
                      <div className="text-muted-foreground">Updated</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">{result.duplicateCount}</div>
                      <div className="text-muted-foreground">Duplicates</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{result.failedCount}</div>
                      <div className="text-muted-foreground">Failed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {result.errors.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2">Errors</h4>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {result.errors.slice(0, 5).map((error, index) => (
                        <div key={index} className="text-sm text-red-600">
                          Row {error.row}: {error.errors.join(', ')}
                        </div>
                      ))}
                      {result.errors.length > 5 && (
                        <div className="text-sm text-muted-foreground">
                          ... and {result.errors.length - 5} more errors
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex space-x-2 pt-4">
                <Button variant="outline" onClick={handleReset} className="flex-1">
                  Import Another File
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}